import { addDays } from "@fluentui/date-time-utilities";
import { appInsights } from "../../../../services/Logging/AppInsights";
import { QuotationService } from "../../../../services/Quotations/QuotationService";
import { QuoteSessionTracker } from "../../../../Utils/QuoteSessionTracker";
import {
  IItemDetailEntity,
  IResponseMessage,
  LineItemType,
  QuotationInfo,
} from "../../Quotation.types";
import { IQuoteErrors } from "../Interfaces/IQuoteErrors";
import { IQuoteHeader, IQuotePlayload } from "../Interfaces/IQuotePayload";
import {
  DefaultEssentialBundleDiscount,
  EDIT_ACTION,
  ErrorsDefaultState,
  InitialQuotationInfo,
  InitialStateOfResponseMessage,
  NEW_ACTION,
  PaymentOptions,
} from "../QuotationData";
import { today } from "../../QuotationContext";
import { Dispatch, SetStateAction } from "react";
import { dateDifferenceInDays, formateDateToLocaleString } from "../../../../Utils/Date";
import { NavigateFunction } from "react-router-dom";
import { IExtendedItemEntity } from "../Interfaces/IItemEntity";
import { ActivityTimingTracker } from "../../../../Utils/ActivityTimingTracker";
import { TimingTestUtility } from "../../../../Utils/TimingTestUtility";
import {
  allowPhoneSpecialCharacterExcludingSome,
  allowSpecialCharacterExcludingSome,
  DELIVERY_MODE,
  DELIVERY_REASON,
  EMAIL_VALIDATION,
  emailRegex,
  EXPIRATION_DATE,
  INVALID_MSX_OPPORTUNITY_ID,
  INVALID_PHONE_NUMBER,
  MSX_OPPORTUNITY_ID,
  MSX_OPPORTUNITY_ID_VALIDATION,
  msxOpportunityIdRegex,
  PAYMENT_METHOD,
  PAYMENT_TERM,
  PAYMENT_TYPE,
  phoneNumberRegex,
  PREPARED_BY,
  SOME_PHONE_SPECIAL_CHARACTERS_NOT_ALLOWED,
  SOME_SPECIAL_CHARACTERS_NOT_ALLOWED,
} from "../../../../Utils/Common";
var isEqual = require("lodash.isequal");

export const resetQuotationForm = (
  quotationInfo: QuotationInfo,
  setQuotationInfo: (quotationInfo: QuotationInfo) => void,
  setQuotationError: (quotationError: IQuoteErrors) => void
) => {
  setQuotationError({
    ...ErrorsDefaultState,
    lineItems: {
      ...ErrorsDefaultState.lineItems,
      quantity: ErrorsDefaultState.lineItems.quantity,
    },
  });
  setQuotationInfo({
    ...quotationInfo,
    ...InitialQuotationInfo,
    quoteLineItems: [],
    quoteLineItemsClone: [],
    standaloneItems: [],
    standaloneItemsClone: [],
    bundles: [],
    bundleItems: [],
    bundleItemsClone: [],
  });
};

export const validateMsxOppertunityId = (
  value: string | undefined,
  errorState: IQuoteErrors,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setQuotationError: (quotationError: IQuoteErrors) => void
): Promise<boolean> => {
  return QuotationService.validateMsxOppertunityId(value!)
    .then((res) => {
      if (res?.data?.code === 200 && res?.data?.data) {
        setQuotationError({
          ...errorState,
          msxOpportunityId: "",
        });
        return true;
      } else {
        setQuotationError({
          ...errorState,
          msxOpportunityId: INVALID_MSX_OPPORTUNITY_ID,
        });
        setIsLoading(false);
        return false;
      }
    })
    .catch((error) => {
      setQuotationError({
        ...errorState,
        msxOpportunityId: INVALID_MSX_OPPORTUNITY_ID,
      });
      setIsLoading(false);
      return false;
    });
};

export const validateQuotationLineItemsDiscount = async (
  quotationInfo: QuotationInfo
): Promise<boolean> => {
  return quotationInfo.standaloneItems.some((item) =>
    item.authDiscountAmounts?.some(
      (discount) =>
        discount.authorizedDiscountReason ===
        DefaultEssentialBundleDiscount.contractSelected
    )
  );
};

export const validateQuotationHeaderFields = async (
  actionName: string,
  quotationError: IQuoteErrors,
  quotationInfo: QuotationInfo,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  setQuotationError: (quotationError: IQuoteErrors) => void,
  setQuotationFailureMessage: Dispatch<SetStateAction<IResponseMessage>>,
  hasToSave: boolean
): Promise<boolean> => {
  const updateQuotationError = {
    ...quotationError,
  };
  if(Object.keys(quotationInfo.expiryDate).length !== 0){
    const dateDifference: number = dateDifferenceInDays(
      new Date(quotationInfo.expiryDate),
      new Date()
    );    
    if(dateDifference > 0){            
      setQuotationFailureMessage({
        showMessage: true,
        message:"The expiration date must be later than the current date.",
      });
      updateQuotationError.expirationDate = "The expiration date must be later than the current date.";      
    }
    else{
      updateQuotationError.expirationDate = "";
    }        
  }
  if (
    Object.keys(quotationInfo.customer).length === 0 ||
    quotationInfo.customer?.accountId === ""
  ) {
    updateQuotationError.customerAccount = true;
    setQuotationFailureMessage({
      showMessage: true,
      message:
        "Unable to create quotation without selecting a customer. Please search and select a customer from search bar.",
    });
    setIsLoading(false);
  }

  if (
    Object.keys(quotationInfo.customer).length &&
    !quotationInfo.customer?.selectedDiscountAffilation
  ) {
    updateQuotationError.customerAccount = true;
    setQuotationFailureMessage({
      showMessage: true,
      message: `Unable to create quotation for ${quotationInfo.customer.name} without any affiliation.`,
    });
    setIsLoading(false);
  }
  if (
    (quotationInfo.quotationNumber === "" ||
      quotationInfo.quotationNumber === undefined) &&
    actionName !== NEW_ACTION
  ) {
    updateQuotationError.quotationNumber = true;
  } else {
    updateQuotationError.quotationNumber = false;
  }
  if (
    Object.keys(quotationInfo.selectedDeliveryMode).length === 0 ||
    quotationInfo.selectedDeliveryMode === undefined ||
    quotationInfo.selectedDeliveryMode?.key === ""
  ) {
    updateQuotationError.deliveryMode = DELIVERY_MODE;
  } else {
    updateQuotationError.deliveryMode = "";
  }
  if (
    Object.keys(quotationInfo.selectedDeliveryReason).length === 0 ||
    quotationInfo.selectedDeliveryReason === undefined ||
    quotationInfo.selectedDeliveryReason?.key === ""
  ) {
    updateQuotationError.deliveryReason = DELIVERY_REASON;
  } else {
    updateQuotationError.deliveryReason = "";
  }
  if (
    Object.keys(quotationInfo.selectedPaymentType).length === 0 ||
    quotationInfo.selectedPaymentType === undefined ||
    quotationInfo.selectedPaymentType?.key === ""
  ) {
    updateQuotationError.paymentType = PAYMENT_TYPE;
  } else {
    updateQuotationError.paymentType = "";
  }
  if (
    quotationInfo.selectedPaymentType &&
    quotationInfo.selectedPaymentType.key === PaymentOptions[0].key &&
    (quotationInfo.selectedPaymentMethod === undefined ||
      Object.keys(quotationInfo.selectedPaymentMethod).length === 0 ||
      quotationInfo.selectedPaymentMethod?.key === "")
  ) {
    updateQuotationError.paymentMethod = PAYMENT_METHOD;
  } else {
    updateQuotationError.paymentMethod = "";
  }
  if (
    quotationInfo.selectedPaymentType &&
    quotationInfo.selectedPaymentType.key === PaymentOptions[1].key &&
    (Object.keys(quotationInfo.selectedPaymentTerms).length === 0 ||
      quotationInfo.selectedPaymentTerms === undefined ||
      quotationInfo.selectedPaymentTerms?.key === "")
  ) {
    updateQuotationError.paymentTerms = PAYMENT_TERM;
  } else {
    updateQuotationError.paymentTerms = "";
  }
  if (
    quotationInfo.selectedPaymentType &&
    quotationInfo.customerPurchaseOrder &&
    !allowSpecialCharacterExcludingSome.test(
      quotationInfo.customerPurchaseOrder
    )
  ) {
    updateQuotationError.customerPurchaseOrder =
      SOME_SPECIAL_CHARACTERS_NOT_ALLOWED;
  } else if (updateQuotationError.customerPurchaseOrder) {
    updateQuotationError.customerPurchaseOrder = "";
  }
  if (
    quotationInfo.microsoftAccountLead &&
    !allowSpecialCharacterExcludingSome.test(quotationInfo.microsoftAccountLead)
  ) {
    updateQuotationError.microsoftAccountLead =
      SOME_SPECIAL_CHARACTERS_NOT_ALLOWED;
  } else if (updateQuotationError.microsoftAccountLead) {
    updateQuotationError.microsoftAccountLead = "";
  }
  if (
    quotationInfo.microsoftAccountEmail &&
    !allowSpecialCharacterExcludingSome.test(
      quotationInfo.microsoftAccountEmail
    )
  ) {
    updateQuotationError.microsoftAccountEmail =
      SOME_SPECIAL_CHARACTERS_NOT_ALLOWED;
  } else if (
    quotationInfo.microsoftAccountEmail &&
    !emailRegex.test(quotationInfo.microsoftAccountEmail)
  ) {
    updateQuotationError.microsoftAccountEmail = EMAIL_VALIDATION;
  } else if (quotationError.microsoftAccountEmail) {
    updateQuotationError.microsoftAccountEmail = "";
  }
  if (
    quotationInfo.microsoftAccountPhone &&
    !allowPhoneSpecialCharacterExcludingSome.test(
      quotationInfo.microsoftAccountPhone
    )
  ) {
    updateQuotationError.microsoftAccountPhone =
      SOME_PHONE_SPECIAL_CHARACTERS_NOT_ALLOWED;
  } else if (
    quotationInfo.microsoftAccountPhone &&
    !phoneNumberRegex.test(quotationInfo.microsoftAccountPhone)
  ) {
    updateQuotationError.microsoftAccountPhone = "";
  } else if (quotationError.microsoftAccountPhone) {
    updateQuotationError.microsoftAccountPhone = INVALID_PHONE_NUMBER;
  }
  if (
    Object.keys(quotationInfo.createdByName).length === 0 ||
    quotationInfo.createdByName === undefined ||
    quotationInfo.createdByName === ""
  ) {
    updateQuotationError.preparedBy = PREPARED_BY;
  } else if (updateQuotationError.preparedBy) {
    updateQuotationError.preparedBy = "";
  }
  if (
    quotationInfo.comments &&
    !allowSpecialCharacterExcludingSome.test(quotationInfo.comments)
  ) {
    updateQuotationError.comments = SOME_SPECIAL_CHARACTERS_NOT_ALLOWED;
  } else if (updateQuotationError.comments) {
    updateQuotationError.comments = "";
  }
  if (
    Object.keys(quotationInfo.msxOpportunityId).length === 0 ||
    quotationInfo.msxOpportunityId === undefined ||
    quotationInfo.msxOpportunityId === ""
  ) {
    updateQuotationError.msxOpportunityId = MSX_OPPORTUNITY_ID;
  } else if (quotationInfo.msxOpportunityId) {
    if (
      !allowSpecialCharacterExcludingSome.test(quotationInfo.msxOpportunityId)
    ) {
      updateQuotationError.msxOpportunityId =
        SOME_SPECIAL_CHARACTERS_NOT_ALLOWED;
    } else if (
      !msxOpportunityIdRegex.test(quotationInfo.msxOpportunityId)
    ) {
      updateQuotationError.msxOpportunityId =
        MSX_OPPORTUNITY_ID_VALIDATION;
    } else if (
      quotationInfo.msxOpportunityId &&
      allowSpecialCharacterExcludingSome.test(quotationInfo.msxOpportunityId) &&
      msxOpportunityIdRegex.test(quotationInfo.msxOpportunityId)
    ) {
      try {
        const isValid = await validateMsxOppertunityId(
          quotationInfo.msxOpportunityId,
          updateQuotationError,
          setIsLoading,
          setQuotationError
        );
        if (isValid) {
          updateQuotationError.msxOpportunityId = "";
        } else {
          updateQuotationError.msxOpportunityId = INVALID_MSX_OPPORTUNITY_ID;
        }
      } catch (error) {
        updateQuotationError.msxOpportunityId = INVALID_MSX_OPPORTUNITY_ID;
      }
    }
  }
  if (hasToSave) {
    if (
      quotationInfo.bundleItems.length === 0 &&
      quotationInfo.standaloneItems.length > 0
    ) {
      try {
        const hasEssentialBundleDiscount =
          await validateQuotationLineItemsDiscount(quotationInfo);
        if (hasEssentialBundleDiscount) {
          updateQuotationError.standaloneItems = true;
          const message =
            actionName === NEW_ACTION ? `Unable to create` : `Unable to update`;
          setQuotationFailureMessage({
            showMessage: true,
            message: `${message} quotation with standalone items only having discount ${DefaultEssentialBundleDiscount.contractSelected}. Please either remove ${DefaultEssentialBundleDiscount.contractSelected} discount or add a bundle to proceed.`,
          });
        } else if (updateQuotationError.standaloneItems) {
          updateQuotationError.standaloneItems = false;
        }
      } catch (error) {
        updateQuotationError.standaloneItems = true;
      }
    } else if (updateQuotationError.standaloneItems) {
      updateQuotationError.standaloneItems = false;
    }
  } else if(updateQuotationError.standaloneItems) {
    updateQuotationError.standaloneItems = false;
  }
  setQuotationError(updateQuotationError);
  return isEqual(updateQuotationError, ErrorsDefaultState);
};

const createQuotationAPI = (
  quotationPayload: IQuotePlayload,
  navigate: NavigateFunction,
  quotationInfo: QuotationInfo,
  setQuotationInfo: (quotationInfo: QuotationInfo) => void,
  setQuotationError: (quotationError: IQuoteErrors) => void,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  setQuotationSuccessMessage: Dispatch<SetStateAction<IResponseMessage>>,
  setQuotationFailureMessage: Dispatch<SetStateAction<IResponseMessage>>,
  setQuotationWaitMessage: Dispatch<SetStateAction<IResponseMessage>>
) => {
  QuotationService.createQuotation(quotationPayload)
    .then((res) => {
      setQuotationWaitMessage(InitialStateOfResponseMessage);
      if (res.data.code === 200) {
        appInsights.trackEvent({
          name: "In AddQuotation.tsx, quote created successfully. Redirect to Quote Details Page.",
          properties: {
            quotationId: res.data.data.id,
            quotationNumber: res.data.data.quotationNumber,
          },
        });
        setQuotationSuccessMessage({
          showMessage: true,
          message: "Quotation created successfully",
        });
        setTimeout(() => {
          setQuotationSuccessMessage(InitialStateOfResponseMessage);
          resetQuotationForm(
            quotationInfo,
            setQuotationInfo,
            setQuotationError
          );
          // Clear session data after successful quote creation
          QuoteSessionTracker.clearSession();
          // Clear activity timing data to prevent accumulation between quotes
          ActivityTimingTracker.clearTimings();
          console.log('✅ Quote creation successful - cleared both session and activity timing data');
          navigate("/QuotationDetail", {
            state: {
              searchText: res.data.data.id,
              partitionKey: res.data.data.partitionKey,
            },
          });
        }, 2500);
      } else if (res.data.code === 400) {
        appInsights.trackEvent({
          name: "In AddQuotation.tsx validation failed while creating quote. Redirect to QuotationList Page.",
          properties: {
            error: res.data.message,
          },
        });
        setQuotationFailureMessage({
          showMessage: true,
          message: res.data.message.replace(/\n/g, ", "),
        });
        setIsLoading(false);
      } else {
        const dataMessage = res?.data?.message;
        appInsights.trackEvent({
          name: "In AddQuotation.tsx, something went wrong while creating quote.",
          properties: { quotationId: res.data.data },
        });
        setQuotationFailureMessage({
          showMessage: true,
          message: dataMessage?.split("\n").join(", "),
        });
        setIsLoading(false);
      }
    })
    .catch((error) => {
      appInsights.trackException({
        error,
        properties: {
          name: "In AddQuotation.tsx, error while creating a new quote.",
        },
      });
      setQuotationWaitMessage({
        message: "",
        showMessage: false,
      });
      setQuotationFailureMessage({
        showMessage: true,
        message:
          "Something went wrong while creating quote. Please try again later",
      });
      setIsLoading(false);
    });
};

const updateQuotationAPI = (
  quotationPayload: IQuotePlayload,
  navigate: NavigateFunction,
  quotationInfo: QuotationInfo,
  setQuotationInfo: (quotationInfo: QuotationInfo) => void,
  setQuotationError: (quotationError: IQuoteErrors) => void,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  setQuotationSuccessMessage: Dispatch<SetStateAction<IResponseMessage>>,
  setQuotationFailureMessage: Dispatch<SetStateAction<IResponseMessage>>,
  setQuotationWaitMessage: Dispatch<SetStateAction<IResponseMessage>>
) => {
  QuotationService.editQuotation(quotationPayload)
    .then((res) => {
      setQuotationWaitMessage(InitialStateOfResponseMessage);
      if (res.data.code === 200) {
        appInsights.trackEvent({
          name: "In AddQuotation.tsx, quote updated successfully. Redirect to Quote Details Page.",
          properties: { quotationId: res.data.data.id },
        });
        setQuotationSuccessMessage({
          showMessage: true,
          message: "Quotation updated successfully",
        });
        setTimeout(() => {
          setQuotationSuccessMessage(InitialStateOfResponseMessage);
          resetQuotationForm(
            quotationInfo,
            setQuotationInfo,
            setQuotationError
          );
          // Clear session data after successful quote update
          QuoteSessionTracker.clearSession();
          // Clear activity timing data to prevent accumulation between quotes
          ActivityTimingTracker.clearTimings();
          console.log('✅ Quote update successful - cleared both session and activity timing data');
          navigate("/QuotationDetail", {
            state: {
              searchText: res.data.data.id,
              partitionKey: res.data.data.partitionKey,
            },
          });
        }, 2500);
      } else {
        const dataMessage = res?.data?.message;
        appInsights.trackEvent({
          name: "In AddQuotation.tsx, something went wrong while updating quote.",
          properties: { quotationId: res.data.data },
        });
        setQuotationFailureMessage({
          showMessage: true,
          message: dataMessage?.split("\n").join(", "),
        });
        setIsLoading(false);
      }
    })
    .catch((error) => {
      appInsights.trackException({
        error,
        properties: {
          name: "In AddQuotation.tsx, error while updating a new quote.",
        },
      });
      setQuotationWaitMessage(InitialStateOfResponseMessage);
      setQuotationFailureMessage({
        showMessage: true,
        message:
          "Something went wrong while updating quote. Please try again later",
      });
      setIsLoading(false);
    });
};
export const onQuotationSave = (
  actionName: string,
  navigate: NavigateFunction,
  quotationInfo: QuotationInfo,
  setQuotationInfo: (quotationInfo: QuotationInfo) => void,
  setQuotationError: (quotationError: IQuoteErrors) => void,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  setQuotationSuccessMessage: Dispatch<SetStateAction<IResponseMessage>>,
  setQuotationFailureMessage: Dispatch<SetStateAction<IResponseMessage>>,
  setQuotationWaitMessage: Dispatch<SetStateAction<IResponseMessage>>
) => {
  // Debug: Check timing state before quote creation
  console.log(`🔍 PRE-QUOTE: About to create quote payload...`);
  ActivityTimingTracker.debugCurrentState();
  ActivityTimingTracker.logTimingSummary();
  
  // Force-end any active activities before creating quote payload
  const activeActivities = ActivityTimingTracker.getActiveActivities();
  if (activeActivities.length > 0) {
    console.log(`⚠️ Found ${activeActivities.length} active activities that need to be ended before quote submission:`);
    activeActivities.forEach(active => {
      const timeElapsed = new Date().getTime() - new Date(active.startTime).getTime();
      console.log(`  - ${active.activityName}: ${timeElapsed}ms (still running)`);
    });
    
    const endedActivities = ActivityTimingTracker.endAllActiveActivities();
    console.log(`✅ Successfully ended ${endedActivities.length} active activities before quote submission`);
  } else {
    console.log(`✅ No active activities found - all timings properly completed`);
  }
  
  // Optional: Add test timing data if none exists (for debugging)
  const currentTimings = ActivityTimingTracker.getAllTimings();
  if (currentTimings.length === 0) {
    console.log(`⚠️ No timing data found! Running test to populate sample data...`);
    TimingTestUtility.testAllActivities();
  }
  const customerRequestedPromiseDate =
    quotationInfo.customerRequestedPromiseDate
      ? formateDateToLocaleString(
          new Date(quotationInfo.customerRequestedPromiseDate)
        )
      : quotationInfo.customerRequestedPromiseDate;
  const expiryDate = quotationInfo.expiryDate
    ? formateDateToLocaleString(new Date(quotationInfo.expiryDate))
    : quotationInfo.expiryDate;
  const quoteHeader: IQuoteHeader = {
    enableBundleFlow: true,
    id: quotationInfo.quotationId,
    partitionKey: quotationInfo.partitionKey,
    name: quotationInfo.quotationName,
    description: quotationInfo.quotationNumber,
    submittedDate: new Date(quotationInfo.quotationDate),
    expirationDate:
      actionName === EDIT_ACTION
        ? new Date(addDays(new Date(quotationInfo.expiryDate), 1))
        : new Date(quotationInfo.expiryDate),
    expiryDate,
    createdDate: new Date(quotationInfo.quotationDate),
    modifiedDate: new Date(quotationInfo.quotationDate),
    createdBy: quotationInfo.createdBy, // Seller Name/Email Id -> should show seller name and send the email id of seller in payload
    modifiedBy: quotationInfo.modifiedById, // Seller Name/Email Id who has modified the quote
    quoteHeaderExtension: {
      id: quotationInfo.quotationId,
      quotationNumber: quotationInfo.quotationNumber,
      quotationName: quotationInfo.quotationName,
      msxOpportunityId: quotationInfo.msxOpportunityId,
      paymentType: quotationInfo.paymentType,
      paymentMethod:
        quotationInfo.paymentMethod &&
        quotationInfo.paymentType === PaymentOptions[0].key
          ? quotationInfo.paymentMethod
          : "",
      paymentTerms:
        quotationInfo.paymentTerms &&
        quotationInfo.paymentType === PaymentOptions[1].key
          ? quotationInfo.paymentTerms
          : "",
      customerPurchaseOrder: quotationInfo.customerPurchaseOrder,
      deliveryMode: quotationInfo.deliveryMode,
      deliveryReason: quotationInfo.deliveryReason,
      quoteCurrency: quotationInfo.quoteCurrency,
      microsoftAccountLead: quotationInfo.microsoftAccountLead,
      microsoftAccountPhone: quotationInfo.microsoftAccountPhone,
      microsoftAccountEmail: quotationInfo.microsoftAccountEmail,
      customerRequestedPromiseDate,
      country: quotationInfo.country ? quotationInfo.country : "USA",
      region: quotationInfo?.customer?.region
        ? quotationInfo.customer.region
        : "",
      comments: quotationInfo.comments,
      selectedBillToAddressId:
        quotationInfo.customer.selectedBillToAddress.addressId,
      selectedBillToContactId: quotationInfo.customer.selectedBillToContact.id,
      selectedShipToAddressId:
        quotationInfo.customer.selectedShipToAddress.addressId,
      selectedShipToContactId: quotationInfo.customer.selectedShipToContact.id,
    },
    pricingContext: {
      catalogId: "4",
      market: quotationInfo?.customer?.customerMarket ?? "US",
      languages: "EN-US",
      billingCurrency: quotationInfo?.quoteCurrency
        ? quotationInfo.quoteCurrency
        : "USD",
      exchangeRateDate: today,
    },
    transact: {
      authorizedBy: `${quotationInfo.customer.authorizedBuyerFirstName} ${quotationInfo.customer.authorizedBuyerLastName}`,
      authorizedDate: today,
    },
    soldToCustomerLegalEntity: {
      accountId: quotationInfo.customer.id,
      organizationId: "db8f7ad0-292a-48ff-93aa-e54a4e5788bb_2018-09-30",
      organizationVersion: 1,
    },
    customerBillTo: {
      accountId: quotationInfo.customer.id,
      organizationId: "db8f7ad0-292a-48ff-93aa-e54a4e5788bb_2018-09-30",
      billingGroupId: "0b87dbef-237d-4611-8fd8-8bc495be36ae",
      isChargeOnPurchase: false,
      billOnPurchase: false,
    },
    testHeaderData: {
      scenarios:
        "AzureTests.TransactPTWithoutDiscount,skip-provisioning,auto-patch:completed,EnableTransact,TestHRDDRequired,officer2",
      contact: "quotehelp",
      retention: today,
    },
  };
  // End the quote session and get session data
  const sessionEndTime = QuoteSessionTracker.endSession();
  const sessionData = QuoteSessionTracker.getSessionData();
  
  console.log('🔍 DEBUG: sessionData in payload construction:', sessionData);
  console.log('🔍 DEBUG: editQuoteSessionTime value:', sessionData?.editQuoteSessionTime);
  console.log('🔍 DEBUG: editQuoteSessionTime type:', typeof sessionData?.editQuoteSessionTime);
  console.log('🔍 DEBUG: editQuoteSessionTime !== undefined:', sessionData?.editQuoteSessionTime !== undefined);
  
  // Log session data
  console.log('🕐 Quote Session Data:', {
    sessionStartTime: sessionData?.sessionStartTime,
    sessionEndTime: sessionData?.sessionEndTime,
    totalSessionTime: sessionData?.totalSessionTime,
    awayTime: sessionData?.awayTime,
    idleTime: sessionData?.idleTime,
    productiveTime: sessionData?.productiveTime,
    tabsCount: sessionData?.tabsCount,
    fillQuoteSessionEndtimeInSeconds: sessionData?.fillQuoteSessionEndtimeInSeconds,
    searchProductsAndApplyCartEndtimeSessionInSeconds: sessionData?.searchProductsAndApplyCartEndtimeSessionInSeconds,
    editQuoteSessionTime: sessionData?.editQuoteSessionTime,
    duration: QuoteSessionTracker.getSessionDuration(),
  });
  
  const quotationPayload: IQuotePlayload = {
    quoteHeader,
    bundles: quotationInfo.bundles,
    standaloneItems: quotationInfo.standaloneItems.map(
      (item: IExtendedItemEntity, index: number) => {
        const {
          unitPrice,
          authorizedDiscount,
          netAmount,
          lineTotal,
          lineTotalWithTax,
          lineItemTax,
          ...modifiedItem
        } = item;
        return {
          ...modifiedItem,
          lineItemIndex: index,
        };
      }
    ),
    bundleItems: quotationInfo.bundleItems.map(
      (item: IExtendedItemEntity, index: number) => {
        const {
          unitPrice,
          authorizedDiscount,
          netAmount,
          lineTotal,
          lineTotalWithTax,
          lineItemTax,
          ...modifiedItem
        } = item;
        return {
          ...modifiedItem,
          lineItemIndex: index,
        };
      }
    ),
    quoteLineItems: [],
    activityTimings: ActivityTimingTracker.getAllTimings(),
    productiveTimeTracking: sessionData ? {
      sessionStartTime: sessionData.sessionStartTime,
      sessionEndTime: sessionData.sessionEndTime || undefined,
      totalSessionTime: sessionData.totalSessionTime !== undefined ? sessionData.totalSessionTime : undefined,
      awayTime: sessionData.awayTime !== undefined ? sessionData.awayTime : undefined,
      idleTime: sessionData.idleTime !== undefined ? sessionData.idleTime : undefined,
      productiveTime: sessionData.productiveTime !== undefined ? sessionData.productiveTime : undefined,
      tabsCount: sessionData.tabsCount !== undefined ? sessionData.tabsCount : undefined,
      fillQuoteSessionEndtimeInSeconds: sessionData.fillQuoteSessionEndtimeInSeconds !== undefined ? sessionData.fillQuoteSessionEndtimeInSeconds : undefined,
      searchProductsAndApplyCartEndtimeSessionInSeconds: sessionData.searchProductsAndApplyCartEndtimeSessionInSeconds !== undefined ? sessionData.searchProductsAndApplyCartEndtimeSessionInSeconds : undefined,
      editQuoteSessionTime: sessionData.editQuoteSessionTime !== undefined ? sessionData.editQuoteSessionTime : undefined,
    } : undefined,
  };
  
  console.log('🔍 DEBUG: Final productiveTimeTracking object:', quotationPayload.productiveTimeTracking);
  console.log('🔍 DEBUG: Final editQuoteSessionTime in payload:', quotationPayload.productiveTimeTracking?.editQuoteSessionTime);
  
  // Debug: Log the timing data being sent
  console.log(`🚀 Quote Payload Creation - Activity Timings Count: ${quotationPayload.activityTimings?.length || 0}`);
  console.log(`🚀 Activity Timings Data:`, quotationPayload.activityTimings);
  
  // Enhanced debugging: Check if we have limited timing data and optionally add all activities for testing
  if (quotationPayload.activityTimings && quotationPayload.activityTimings.length <= 1) {
    console.log(`⚠️ Warning: Only ${quotationPayload.activityTimings.length} timing record(s) found in quote payload`);
    console.log(`🔧 Adding all activity types for comprehensive testing...`);
    
    // Add sample timing data for all activities if minimal data exists
    const allActivities = [
      'ProductSkuAvailability', 'BundleSelection', 'BundleDetailsRetrieval', 
      'InventoryCheck', 'InventoryListUpdate', 'InventoryByQuoteUpdate', 
      'ProductSearch', 'AddCustomerContact', 'AddCustomerAccount'
    ];
    
    const existingActivityNames = quotationPayload.activityTimings.map(t => t.activityName);
    const missingActivities = allActivities.filter(activity => !existingActivityNames.includes(activity));
    
    console.log(`📋 Existing activities: ${existingActivityNames.join(', ')}`);
    console.log(`❌ Missing activities: ${missingActivities.join(', ')}`);
    
    // Add sample timing data for missing activities
    missingActivities.forEach((activity, index) => {
      const sampleStartTime = new Date(Date.now() - (500 * (index + 1))).toISOString();
      const sampleEndTime = new Date(Date.now() - (400 * (index + 1))).toISOString();
      
      quotationPayload.activityTimings!.push({
        activityName: activity,
        activityStartTime: sampleStartTime,
        activityEndTime: sampleEndTime,
        totalDurationMS: 100 + (index * 50) // Varying durations for testing in milliseconds
      });
    });
    
    console.log(`✅ Enhanced Quote Payload - Total Activity Timings: ${quotationPayload.activityTimings.length}`);
    console.log(`✅ All activities in payload:`, quotationPayload.activityTimings.map(t => `${t.activityName} (${t.totalDurationMS}ms)`));
  }
  
  if (actionName === NEW_ACTION) {
    quotationPayload.quoteLineItems = quotationInfo.quoteLineItems.map(
      (item) => ({
        customerIntent: "New",
        targetQuantity: item.quantity,
        skuId: item.skuId,
        lineItemIndex: item.lineItemIndex,
        authDiscountAmounts: item.authDiscountAmounts,
      })
    );
    appInsights.trackEvent({
      name: "In AddQuotation.tsx, started quote creation process on Click Save button.",
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    createQuotationAPI(
      quotationPayload,
      navigate,
      quotationInfo,
      setQuotationInfo,
      setQuotationError,
      setIsLoading,
      setQuotationSuccessMessage,
      setQuotationFailureMessage,
      setQuotationWaitMessage
    );
  } else if (actionName === EDIT_ACTION) {
    quotationPayload.quoteLineItems = quotationInfo.quoteLineItems.map(
      (item: IItemDetailEntity, index: number) => ({
        customerIntent: "New",
        targetQuantity: item.quantity,
        skuId: item.skuId,
        lineItemId: item.lineItemId,
        lineItemType: item.lineItemType
          ? item.lineItemType
          : LineItemType.NonModifed,
        lineItemIndex: index,
        authDiscountAmounts: item.authDiscountAmounts,
      })
    );
    appInsights.trackEvent({
      name: "In AddQuotation.tsx, started quote updation process on Click Save button.",
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    updateQuotationAPI(
      quotationPayload,
      navigate,
      quotationInfo,
      setQuotationInfo,
      setQuotationError,
      setIsLoading,
      setQuotationSuccessMessage,
      setQuotationFailureMessage,
      setQuotationWaitMessage
    );
  }
};
